import { InputHTMLAttributes, useRef } from 'react'
import {
  FieldValues,
  UseFormRegister,
  Path,
  RegisterOptions,
  GlobalError,
  FieldErrorsImpl,
} from 'react-hook-form'
import { NAMESPACE } from '../utils'

interface FieldErrors<T extends FieldValues> {
  root?: Record<string, GlobalError> & GlobalError
  [key: string]: FieldErrorsImpl<T> | GlobalError | undefined
}

interface ToggleButtonPropTypes<T extends FieldValues>
  extends InputHTMLAttributes<HTMLInputElement> {
  name: Path<T>
  register?: UseFormRegister<T>
  validation?: RegisterOptions<T, Path<T>>
  errors?: FieldErrors<T>
  dataQaId?: string
  className?: string
}

const ToggleButton = <T extends FieldValues>({
  dataQaId = '',
  name,
  register,
  validation = {},
  errors,
  disabled = false,
  checked,
  className = '',
  ...rest
}: ToggleButtonPropTypes<T>) => {
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div data-qa-id={dataQaId || ''} className={className}>
      <div className="block">
        <label className="relative block w-[1.85rem] h-[1.5rem]">
          <input
            className="hidden peer w-[1.063rem]"
            type="checkbox"
            {...(register ? register(name, { ...validation }) : { name })}
            {...rest}
            disabled={disabled}
            checked={checked}
            value={`${checked}`}
            ref={inputRef}
          />
          <span
            className={`absolute block cursor-pointer h-[0.775rem] duration-300 ease-in-out top-0 left-0 right-0 bottom-0 transform translate-y-1/2 bg-secondary-150 peer-checked:bg-secondary-150 before:absolute before:content-[""] before:w-[1.175rem] before:h-[1.175rem] before:bg-accent-0 before:translate-x-[0px] before:translate-y-[-18%] peer-checked:before:bg-secondary-450 peer-checked:before:translate-x-[0.875rem] before:duration-300 before:ease-in-out rounded-full before:rounded-full before:shadow-[0px_0px_0.25rem_0px_rgba(0,_0,_0,_0.2)] ${
              disabled ? 'opacity-40 !cursor-not-allowed' : ''
            } ' : ''
            }`}
            role="checkbox"
            tabIndex={rest?.tabIndex ?? 0}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
                e.preventDefault()
                inputRef.current?.click()
              }
            }}
          />
        </label>
      </div>

      {errors &&
        name &&
        errors?.[name] &&
        errors?.[name]?.['type'] === 'required' && (
          <span className="text-error-500 text-xxs font-primary font-extraLight">
            {NAMESPACE?.REQUIRED_FIELD}
          </span>
        )}
      {errors && name && errors?.[name] && (
        <span className="text-error-500 text-xs">
          {errors?.[name]?.['message']?.toString()}
        </span>
      )}
    </div>
  )
}

export default ToggleButton
